/**
 * Composable for formatting phone numbers in international format
 * Example: +33123456789 -> +33 1 23 45 67 89
 */
export const usePhoneFormatter = () => {
  /**
   * Formats a phone number in international format with grouped spaces
   * @param phone - The phone number to format (e.g., "+33123456789", "0123456789")
   * @param countryCode - Optional country code to prepend if not present (e.g., "33" for France)
   * @returns Formatted phone number (e.g., "+33 1 23 45 67 89") or empty string if invalid
   */
  const formatPhone = (phone: string | null | undefined, countryCode?: string): string => {
    if (!phone) return ''

    // Remove all non-digit characters except leading +
    let cleaned = phone.replace(/[^\d+]/g, '')

    // If no + prefix and starts with 0, replace with country code
    if (!cleaned.startsWith('+') && cleaned.startsWith('0') && countryCode) {
      cleaned = `+${countryCode}${cleaned.slice(1)}`
    }

    // If no + prefix, add it with country code if provided
    if (!cleaned.startsWith('+') && countryCode) {
      cleaned = `+${countryCode}${cleaned}`
    }

    // Extract the + prefix if present
    const hasPlus = cleaned.startsWith('+')
    const digits = cleaned.replace('+', '')

    if (digits.length < 4) return phone // Too short, return original

    // Format based on length and country patterns
    const formatted = formatDigits(digits)

    return hasPlus ? `+${formatted}` : formatted
  }

  /**
   * Formats digits into groups for better readability
   */
  const formatDigits = (digits: string): string => {
    // French numbers: +33 X XX XX XX XX (10 digits after country code)
    if (digits.startsWith('33') && digits.length === 11) {
      return `${digits.slice(0, 2)} ${digits.slice(2, 3)} ${digits.slice(3, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)} ${digits.slice(9, 11)}`
    }

    // Belgian numbers: +32 XXX XX XX XX
    if (digits.startsWith('32') && digits.length >= 10) {
      return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 7)} ${digits.slice(7, 9)} ${digits.slice(9)}`
    }

    // US/Canada numbers: +1 XXX XXX XXXX
    if (digits.startsWith('1') && digits.length === 11) {
      return `${digits.slice(0, 1)} ${digits.slice(1, 4)} ${digits.slice(4, 7)} ${digits.slice(7, 11)}`
    }

    // Generic international format: country code (2-3 digits) + groups of 2-3
    const countryCodeLength = digits.length > 10 ? (digits.startsWith('1') ? 1 : 2) : 0
    const countryPart = digits.slice(0, countryCodeLength)
    const rest = digits.slice(countryCodeLength)

    // Group remaining digits by 2
    const groups = rest.match(/.{1,2}/g) ?? []
    return countryPart ? `${countryPart} ${groups.join(' ')}` : groups.join(' ')
  }

  /**
   * Checks if a string looks like a valid phone number
   */
  const isValidPhone = (phone: string | null | undefined): boolean => {
    if (!phone) return false
    const cleaned = phone.replace(/[^\d+]/g, '')
    return cleaned.length >= 8 && cleaned.length <= 15
  }

  return {
    formatPhone,
    isValidPhone,
  }
}
